import { memo, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { Currency } from '@pancakeswap/sdk'
import { Flex, Spinner, Text } from '@pancakeswap/uikit'
import { useActiveChainId } from 'hooks/useActiveChainId'

const NETWORK_SLUG: Record<number, string> = {
  369: 'pulsechain',
  1: 'eth',
}

const ChartCard = styled.div`
  width: 100%;
  min-height: 520px;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.backgroundAlt};
  box-shadow: 0 16px 40px -16px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
`

const PoolBar = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
  cursor: pointer;
  transition: color 200ms ease;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const StyledIframe = styled.iframe`
  width: 100%;
  min-height: 520px;
  border: 0;
  display: block;
  flex: 1;
`

interface Props {
  inputCurrency?: Currency | null
  outputCurrency?: Currency | null
  height?: string
}

export const GeckoTerminalChart = memo(function GeckoTerminalChart({ inputCurrency, outputCurrency, height }: Props) {
  const { chainId } = useActiveChainId()
  const network = NETWORK_SLUG[chainId] ?? 'pulsechain'

  const inputAddress = inputCurrency?.wrapped?.address
  const outputAddress = outputCurrency?.wrapped?.address

  const [pool, setPool] = useState<{ address: string; name: string } | undefined>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchPools = async (address: string): Promise<any[]> => {
      try {
        const res = await fetch(`https://api.geckoterminal.com/api/v2/networks/${network}/tokens/${address}/pools?page=1`)
        const json = await res.json()
        return json?.data ?? []
      } catch {
        return []
      }
    }

    const containsToken = (candidate: any, address?: string) => {
      if (!address) return false
      const base = candidate?.relationships?.base_token?.data?.id?.toLowerCase() ?? ''
      const quote = candidate?.relationships?.quote_token?.data?.id?.toLowerCase() ?? ''
      return base.endsWith(address.toLowerCase()) || quote.endsWith(address.toLowerCase())
    }

    // pair pool first (checked from both tokens' pool lists), then output token's top pool, then input's
    async function findPool() {
      if (!inputAddress && !outputAddress) {
        setPool(undefined)
        return
      }
      setLoading(true)

      const outputPools = outputAddress ? await fetchPools(outputAddress) : []
      if (cancelled) return
      let best = outputPools.find((p) => containsToken(p, inputAddress))

      let inputPools: any[] = []
      if (!best && inputAddress) {
        inputPools = await fetchPools(inputAddress)
        if (cancelled) return
        best = inputPools.find((p) => containsToken(p, outputAddress))
      }

      best = best ?? outputPools[0] ?? inputPools[0]
      setPool(
        best?.attributes?.address
          ? { address: best.attributes.address, name: best.attributes.name ?? 'Pool' }
          : undefined,
      )
      setLoading(false)
    }

    findPool()
    return () => {
      cancelled = true
    }
  }, [network, inputAddress, outputAddress])

  return (
    <ChartCard style={height ? { minHeight: height } : undefined}>
      {pool ? (
        <>
          <StyledIframe
            title="GeckoTerminal chart"
            src={`https://www.geckoterminal.com/${network}/pools/${pool.address}?embed=1&info=0&swaps=0&grayscale=0&light_chart=0&bg_color=111113`}
            allow="clipboard-write"
            allowFullScreen
            style={height ? { minHeight: height } : undefined}
          />
          <PoolBar
            href={`https://www.geckoterminal.com/${network}/pools/${pool.address}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{pool.name}</span>
            <span>
              {pool.address.slice(0, 6)}…{pool.address.slice(-4)} ↗
            </span>
          </PoolBar>
        </>
      ) : (
        <Flex flex="1" alignItems="center" justifyContent="center" flexDirection="column" style={{ gap: '12px' }}>
          {loading ? (
            <Spinner size={48} />
          ) : (
            <Text color="textSubtle" fontSize="14px">
              No chart available for this pair
            </Text>
          )}
        </Flex>
      )}
    </ChartCard>
  )
})

export default GeckoTerminalChart
